<template>
  <div class="teaching-service-container">
    <!-- Hero Header Section -->
    <v-card class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-school</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">ระยะเวลาปฏิบัติการสอน</h1>
          <p class="hero-subtitle">3. การปฏิบัติหน้าที่สอนในสถานศึกษาปัจจุบัน</p>
        </div>
        <v-spacer></v-spacer>
        <v-chip class="position-chip" large>
          <v-icon left color="#dc2626">mdi-account-tie</v-icon>
          {{ user_status_type }}
        </v-chip>
      </div>
    </v-card>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64" color="#dc2626"></v-progress-circular>
    </v-overlay>

    <!-- Main Statistics Cards -->
    <v-row class="stats-row mb-6">
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-1" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="32">mdi-calendar-star</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ teachingExperienceYears }}</span>
            <span class="stat-unit">ปี</span>
            <span class="stat-label">ประสบการณ์สอนรวม</span>
          </div>
          <div class="stat-ring"></div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-2" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="32">mdi-school-outline</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ currentSchoolExperienceYears }}</span>
            <span class="stat-unit">ปี</span>
            <span class="stat-label">สถานศึกษาปัจจุบัน</span>
          </div>
          <div class="stat-ring"></div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-3" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="32">mdi-counter</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ totalDaysInServiceNum.toLocaleString() }}</span>
            <span class="stat-unit">วัน</span>
            <span class="stat-label">รวมในราชการ</span>
          </div>
          <div class="stat-ring"></div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card stat-card-4" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="32">mdi-percent</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ percentageInCurrentSchool }}</span>
            <span class="stat-unit">%</span>
            <span class="stat-label">ณ สถานศึกษานี้</span>
          </div>
          <div class="stat-ring"></div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Cards -->
    <v-row>
      <!-- อายุราชการรวม -->
      <v-col cols="12" lg="6">
        <v-card class="info-card service-card" elevation="0">
          <div class="card-header service-header">
            <div class="header-icon">
              <v-icon color="white" size="24">mdi-calendar-account</v-icon>
            </div>
            <div class="header-text">
              <span class="header-title">อายุราชการรวม</span>
              <span class="header-subtitle">ข้อมูลตั้งแต่วันบรรจุราชการ</span>
            </div>
          </div>

          <v-card-text class="card-body">
            <div class="info-row">
              <div class="info-icon-wrapper">
                <v-icon color="#dc2626" size="22">mdi-calendar-check</v-icon>
              </div>
              <div class="info-content">
                <span class="info-label">วันที่บรรจุราชการ</span>
                <span class="info-value">{{ formattedAppointmentDate }}</span>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="duration-display">
              <div class="duration-label">
                <v-icon color="#2e7d32" class="mr-2" size="20">mdi-timer-outline</v-icon>
                รวมระยะเวลาปฏิบัติราชการ
              </div>
              <div class="duration-boxes">
                <div class="duration-box">
                  <span class="duration-number">{{ serviceDiff.years }}</span>
                  <span class="duration-unit">ปี</span>
                </div>
                <div class="duration-dot"></div>
                <div class="duration-box">
                  <span class="duration-number">{{ serviceDiff.months }}</span>
                  <span class="duration-unit">เดือน</span>
                </div>
                <div class="duration-dot"></div>
                <div class="duration-box">
                  <span class="duration-number">{{ serviceDiff.days }}</span>
                  <span class="duration-unit">วัน</span>
                </div>
              </div>
            </div>

            <div class="total-badge mt-4">
              <v-icon color="#dc2626" size="18" class="mr-2">mdi-sigma</v-icon>
              <span>รวมทั้งหมด</span>
              <v-chip color="#fef2f2" text-color="#dc2626" class="ml-auto font-weight-bold" small>
                {{ totalDaysInServiceNum.toLocaleString() }} วัน
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- สถานศึกษาปัจจุบัน -->
      <v-col cols="12" lg="6">
        <v-card class="info-card current-card" elevation="0">
          <div class="card-header current-header">
            <div class="header-icon">
              <v-icon color="white" size="24">mdi-school</v-icon>
            </div>
            <div class="header-text">
              <span class="header-title">สถานศึกษาปัจจุบัน</span>
              <span class="header-subtitle">ระยะเวลาปฏิบัติงาน ณ หน่วยงานปัจจุบัน</span>
            </div>
          </div>

          <v-card-text class="card-body">
            <div class="info-row">
              <div class="info-icon-wrapper info-icon-orange">
                <v-icon color="#a86400" size="22">mdi-calendar-start</v-icon>
              </div>
              <div class="info-content">
                <span class="info-label">วันที่เริ่มปฏิบัติงาน</span>
                <span class="info-value info-value-orange">{{ formattedCurrentSchoolDate }}</span>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="duration-display duration-display-orange">
              <div class="duration-label">
                <v-icon color="#455a64" class="mr-2" size="20">mdi-clock-outline</v-icon>
                รวมระยะเวลา ณ สถานศึกษาปัจจุบัน
              </div>
              <div class="duration-boxes duration-boxes-orange">
                <div class="duration-box duration-box-orange">
                  <span class="duration-number">{{ currentDiff.years }}</span>
                  <span class="duration-unit">ปี</span>
                </div>
                <div class="duration-dot duration-dot-orange"></div>
                <div class="duration-box duration-box-orange">
                  <span class="duration-number">{{ currentDiff.months }}</span>
                  <span class="duration-unit">เดือน</span>
                </div>
                <div class="duration-dot duration-dot-orange"></div>
                <div class="duration-box duration-box-orange">
                  <span class="duration-number">{{ currentDiff.days }}</span>
                  <span class="duration-unit">วัน</span>
                </div>
              </div>
            </div>

            <div class="total-badge total-badge-orange mt-4">
              <v-icon color="#a86400" size="18" class="mr-2">mdi-sigma</v-icon>
              <span>รวมทั้งหมด</span>
              <v-chip color="#fff4dc" text-color="#a86400" class="ml-auto font-weight-bold" small>
                {{ totalDaysInCurrentSchoolNum.toLocaleString() }} วัน
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Progress & Alerts Section -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card class="progress-card" elevation="0">
          <v-card-text class="pa-6">
            <div class="progress-header mb-4">
              <div class="d-flex align-center">
                <v-icon color="#dc2626" class="mr-3" size="24">mdi-chart-donut</v-icon>
                <div>
                  <div class="progress-title">สัดส่วนการปฏิบัติงาน</div>
                  <div class="progress-subtitle">เปรียบเทียบระยะเวลาในสถานศึกษาปัจจุบันกับอายุราชการรวม</div>
                </div>
              </div>
            </div>

            <div class="progress-visual">
              <div class="progress-bar-container">
                <div class="progress-bar-bg">
                  <div class="progress-bar-fill" :style="{ width: percentageInCurrentSchool + '%' }"></div>
                </div>
                <div class="progress-labels">
                  <span class="progress-start">0%</span>
                  <span class="progress-current">{{ percentageInCurrentSchool }}%</span>
                  <span class="progress-end">100%</span>
                </div>
              </div>

              <div class="progress-legend mt-4">
                <div class="legend-item">
                  <div class="legend-dot legend-dot-red"></div>
                  <span>สถานศึกษาปัจจุบัน ({{ totalDaysInCurrentSchoolNum.toLocaleString() }} วัน)</span>
                </div>
                <div class="legend-item">
                  <div class="legend-dot legend-dot-gray"></div>
                  <span>สถานศึกษาอื่น ({{ (totalDaysInServiceNum - totalDaysInCurrentSchoolNum).toLocaleString() }} วัน)</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Status Alerts -->
    <v-row class="mt-4" v-if="isNewTeacher || isExperiencedTeacher">
      <v-col cols="12">
        <v-card
          v-if="isNewTeacher"
          class="status-alert status-alert-info"
          elevation="0"
        >
          <div class="alert-content">
            <div class="alert-icon">
              <v-icon color="#455a64" size="28">mdi-account-star</v-icon>
            </div>
            <div class="alert-text">
              <div class="alert-title">ครูใหม่</div>
              <div class="alert-desc">คุณเป็นครูใหม่ในสถานศึกษานี้ (อายุงานน้อยกว่า 1 ปี)</div>
            </div>
            <v-chip color="#e4e4e7" text-color="#455a64" class="alert-badge">
              <v-icon left small>mdi-star-outline</v-icon>
              น้อยกว่า 1 ปี
            </v-chip>
          </div>
        </v-card>

        <v-card
          v-if="isExperiencedTeacher"
          class="status-alert status-alert-success"
          elevation="0"
        >
          <div class="alert-content">
            <div class="alert-icon">
              <v-icon color="#2e7d32" size="28">mdi-medal</v-icon>
            </div>
            <div class="alert-text">
              <div class="alert-title">ครูผู้มีประสบการณ์</div>
              <div class="alert-desc">คุณมีประสบการณ์ในสถานศึกษานี้มากกว่า 5 ปี</div>
            </div>
            <v-chip color="#e8f5e9" text-color="#1b5e20" class="alert-badge">
              <v-icon left small>mdi-trophy</v-icon>
              มากกว่า 5 ปี
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detail Expansion Panel -->
    <v-card class="detail-card mt-6" elevation="0">
      <v-expansion-panels flat class="detail-panels">
        <v-expansion-panel>
          <v-expansion-panel-header class="detail-header">
            <div class="d-flex align-center">
              <div class="detail-icon-wrapper mr-3">
                <v-icon color="white" size="20">mdi-chart-timeline-variant</v-icon>
              </div>
              <div>
                <div class="detail-title">สถิติและข้อมูลเพิ่มเติม</div>
                <div class="detail-subtitle">รายละเอียดการคำนวณระยะเวลา</div>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="detail-content">
            <v-row>
              <v-col cols="12" md="6" lg="3">
                <div class="stat-mini-card stat-mini-blue">
                  <v-icon color="#455a64" size="32" class="mb-2">mdi-calendar-today</v-icon>
                  <div class="stat-mini-value">{{ formattedCurrentDate }}</div>
                  <div class="stat-mini-label">วันที่คำนวณ</div>
                </div>
              </v-col>
              <v-col cols="12" md="6" lg="3">
                <div class="stat-mini-card stat-mini-red">
                  <v-icon color="#dc2626" size="32" class="mb-2">mdi-briefcase-clock</v-icon>
                  <div class="stat-mini-value">{{ teachingExperienceYears }} ปี</div>
                  <div class="stat-mini-label">ประสบการณ์สอนรวม</div>
                </div>
              </v-col>
              <v-col cols="12" md="6" lg="3">
                <div class="stat-mini-card stat-mini-orange">
                  <v-icon color="#a86400" size="32" class="mb-2">mdi-school</v-icon>
                  <div class="stat-mini-value">{{ currentSchoolExperienceYears }} ปี</div>
                  <div class="stat-mini-label">ประสบการณ์ ณ สถานศึกษานี้</div>
                </div>
              </v-col>
              <v-col cols="12" md="6" lg="3">
                <div class="stat-mini-card stat-mini-green">
                  <v-icon color="#2e7d32" size="32" class="mb-2">mdi-chart-arc</v-icon>
                  <div class="stat-mini-value">{{ percentageInCurrentSchool }}%</div>
                  <div class="stat-mini-label">สัดส่วนในสถานศึกษานี้</div>
                </div>
              </v-col>
            </v-row>

            <!-- Timeline -->
            <div class="timeline-section mt-6">
              <div class="timeline-title mb-4">
                <v-icon color="#dc2626" class="mr-2">mdi-timeline-clock-outline</v-icon>
                Timeline การปฏิบัติงาน
              </div>
              <div class="timeline">
                <div class="timeline-item">
                  <div class="timeline-dot start"></div>
                  <div class="timeline-content">
                    <span class="timeline-label">วันบรรจุราชการ</span>
                    <span class="timeline-date">{{ formattedAppointmentDate }}</span>
                  </div>
                </div>
                <div class="timeline-line"></div>
                <div class="timeline-item">
                  <div class="timeline-dot middle"></div>
                  <div class="timeline-content">
                    <span class="timeline-label">เริ่มงาน ณ สถานศึกษาปัจจุบัน</span>
                    <span class="timeline-date">{{ formattedCurrentSchoolDate }}</span>
                  </div>
                </div>
                <div class="timeline-line"></div>
                <div class="timeline-item">
                  <div class="timeline-dot current"></div>
                  <div class="timeline-content">
                    <span class="timeline-label">ปัจจุบัน</span>
                    <span class="timeline-date">{{ formattedCurrentDate }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'HRvecTeachingService',

  data() {
    return {
      ApiKey: 'HRvec2021',
      users: {},
      loading: false
    }
  },

  async mounted() {
    await this.loadUserData()
  },

  methods: {
    async loadUserData() {
      try {
        this.loading = true
        const userSession = JSON.parse(localStorage.getItem('user')) || {}

        if (!userSession.id_card) {
          throw new Error('ไม่พบข้อมูลผู้ใช้ในระบบ')
        }

        const result = await this.$http.post('personnel_temporary.php', {
          ApiKey: this.ApiKey,
          id_card: userSession.id_card
        })

        this.users = result.data || {}
      } catch (error) {
        console.error('Error loading user data:', error)
      } finally {
        this.loading = false
      }
    },

    calculateDateDifference(startDate, endDate) {
      if (!startDate || !endDate) return { years: 0, months: 0, days: 0 }

      const start = new Date(startDate)
      const end = new Date(endDate)

      if (start > end) {
        return { years: 0, months: 0, days: 0 }
      }

      let years = end.getFullYear() - start.getFullYear()
      let months = end.getMonth() - start.getMonth()
      let days = end.getDate() - start.getDate()

      if (days < 0) {
        months--
        const lastMonth = new Date(end.getFullYear(), end.getMonth(), 0)
        days += lastMonth.getDate()
      }

      if (months < 0) {
        years--
        months += 12
      }

      return { years, months, days }
    },

    calculateTotalDays(startDate, endDate) {
      if (!startDate || !endDate) return 0

      const start = new Date(startDate)
      const end = new Date(endDate)
      const diffTime = Math.abs(end - start)
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    },

    convertBuddhistToChristian(buddhistYear) {
      return parseInt(buddhistYear) - 543
    },

    isValidDate(date) {
      return date instanceof Date && !isNaN(date)
    },

    formatDate(date) {
      if (!date) return 'ไม่ระบุ'

      const dateObj = new Date(date)
      if (!this.isValidDate(dateObj)) return 'วันที่ไม่ถูกต้อง'

      return dateObj.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },

  computed: {
    user_status_type() {
      if (!this.users.user_status) return 'ไม่ระบุ'

      const statusMap = {
        teacher: 'ข้าราชการครู',
        director: 'ผู้อำนวยการวิทยาลัย',
        se_director: 'รองผู้อำนวยการวิทยาลัย',
        S: 'ศึกษานิเทศก์'
      }

      return statusMap[this.users.user_status] || 'ไม่ระบุ'
    },

    appointmentDateISO() {
      if (!this.users.appoin_year || !this.users.appoin_month || !this.users.appoin_day) {
        return null
      }

      const year = this.convertBuddhistToChristian(this.users.appoin_year)
      const month = String(this.users.appoin_month).padStart(2, '0')
      const day = String(this.users.appoin_day).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    formattedAppointmentDate() {
      return this.formatDate(this.appointmentDateISO)
    },

    formattedCurrentSchoolDate() {
      if (!this.users.date_app_now) return 'ไม่ระบุ'
      return this.formatDate(this.users.date_app_now)
    },

    formattedCurrentDate() {
      return new Date().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    serviceDiff() {
      if (!this.appointmentDateISO) return { years: 0, months: 0, days: 0 }
      return this.calculateDateDifference(this.appointmentDateISO, new Date())
    },

    currentDiff() {
      if (!this.users.date_app_now) return { years: 0, months: 0, days: 0 }
      return this.calculateDateDifference(this.users.date_app_now, new Date())
    },

    get_gov_Age() {
      if (!this.appointmentDateISO) return 'ไม่สามารถคำนวณได้'
      const diff = this.serviceDiff
      return `${diff.years} ปี ${diff.months} เดือน ${diff.days} วัน`
    },

    get_Appoint_Age() {
      if (!this.users.date_app_now) return 'ไม่สามารถคำนวณได้'
      const diff = this.currentDiff
      return `${diff.years} ปี ${diff.months} เดือน ${diff.days} วัน`
    },

    totalDaysInServiceNum() {
      if (!this.appointmentDateISO) return 0
      return this.calculateTotalDays(this.appointmentDateISO, new Date())
    },

    totalDaysInCurrentSchoolNum() {
      if (!this.users.date_app_now) return 0
      return this.calculateTotalDays(this.users.date_app_now, new Date())
    },

    percentageInCurrentSchool() {
      const totalDays = this.totalDaysInServiceNum
      const currentSchoolDays = this.totalDaysInCurrentSchoolNum

      if (totalDays === 0) return 0
      return Math.round((currentSchoolDays / totalDays) * 100)
    },

    teachingExperienceYears() {
      if (!this.appointmentDateISO) return 0
      const diff = this.calculateDateDifference(this.appointmentDateISO, new Date())
      return diff.years
    },

    currentSchoolExperienceYears() {
      if (!this.users.date_app_now) return 0
      const diff = this.calculateDateDifference(this.users.date_app_now, new Date())
      return diff.years
    },

    isNewTeacher() {
      return this.currentSchoolExperienceYears < 1
    },

    isExperiencedTeacher() {
      return this.currentSchoolExperienceYears >= 5
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap');

.teaching-service-container {
  font-family: 'Prompt', sans-serif;
  padding: 24px;
  background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
  min-height: 100vh;
}

/* Hero Header */
.hero-header {
  background: var(--hv-hero);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 40px rgba(220, 38, 38, 0.3);
}

.hero-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.hero-text {
  color: white;
}

.hero-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 6px 0;
  letter-spacing: -0.3px;
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.position-chip {
  background: white !important;
  color: #263238 !important;
  font-weight: 500;
  padding: 0 20px;
  height: 44px;
}

/* Statistics Cards */
.stats-row {
  margin-top: -16px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
  height: 100%;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.1);
}

.stat-ring {
  position: absolute;
  top: -30px;
  right: -30px;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  opacity: 0.1;
}

.stat-card-1 .stat-ring { background: #dc2626; }
.stat-card-2 .stat-ring { background: #a86400; }
.stat-card-3 .stat-ring { background: #455a64; }
.stat-card-4 .stat-ring { background: #2e7d32; }

.stat-icon {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card-1 .stat-icon { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
.stat-card-2 .stat-icon { background: linear-gradient(135deg, #a86400 0%, #a86400 100%); }
.stat-card-3 .stat-icon { background: linear-gradient(135deg, #455a64 0%, #455a64 100%); }
.stat-card-4 .stat-icon { background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%); }

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 36px;
  font-weight: 700;
  color: #263238;
  line-height: 1;
}

.stat-unit {
  font-size: 16px;
  color: #6b7280;
  font-weight: 500;
  margin-left: 4px;
}

.stat-label {
  font-size: 13px;
  color: #9ca3af;
  margin-top: 6px;
}

/* Info Cards */
.info-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  height: 100%;
}

.card-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.service-header {
  background: var(--hv-header-navy);
}

.current-header {
  background: linear-gradient(135deg, #a86400 0%, #a86400 100%);
}

.header-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.header-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 300;
}

.card-body {
  padding: 24px !important;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.info-icon-wrapper {
  width: 48px;
  height: 48px;
  background: #fef2f2;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-icon-orange {
  background: #fff4dc;
}

.info-content {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 400;
}

.info-value {
  font-size: 17px;
  color: #dc2626;
  font-weight: 600;
  margin-top: 2px;
}

.info-value-orange {
  color: #a86400;
}

/* Duration Display */
.duration-display {
  background: #fafafa;
  border-radius: 16px;
  padding: 20px;
}

.duration-label {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.duration-boxes {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.duration-box {
  text-align: center;
  padding: 14px 20px;
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border: 2px solid #fef2f2;
}

.duration-box-orange {
  border-color: #fff4dc;
}

.duration-number {
  font-size: 32px;
  font-weight: 700;
  color: #dc2626;
  display: block;
  line-height: 1;
}

.duration-boxes-orange .duration-number {
  color: #a86400;
}

.duration-unit {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  margin-top: 4px;
  display: block;
}

.duration-dot {
  width: 8px;
  height: 8px;
  background: #dc2626;
  border-radius: 50%;
}

.duration-dot-orange {
  background: #a86400;
}

/* Total Badge */
.total-badge {
  background: #fef2f2;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #991b1b;
  font-weight: 500;
}

.total-badge-orange {
  background: #fff4dc;
  color: #a86400;
}

/* Progress Card */
.progress-card {
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.progress-title {
  font-size: 16px;
  font-weight: 600;
  color: #263238;
}

.progress-subtitle {
  font-size: 12px;
  color: #6b7280;
}

.progress-bar-container {
  position: relative;
}

.progress-bar-bg {
  height: 24px;
  background: #f3f4f6;
  border-radius: 12px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #dc2626 0%, #a86400 100%);
  border-radius: 12px;
  transition: width 1s ease;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #9ca3af;
}

.progress-current {
  color: #dc2626;
  font-weight: 600;
}

.progress-legend {
  display: flex;
  gap: 24px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
}

.legend-dot-red {
  background: linear-gradient(135deg, #dc2626 0%, #a86400 100%);
}

.legend-dot-gray {
  background: #e5e7eb;
}

/* Status Alerts */
.status-alert {
  border-radius: 16px;
  overflow: hidden;
}

.status-alert-info {
  background: linear-gradient(135deg, #e4e4e7 0%, #f5f5f5 100%);
  border: 1px solid #e4e4e7;
}

.status-alert-success {
  background: linear-gradient(135deg, #e8f5e9 0%, #e8f5e9 100%);
  border: 1px solid #81c784;
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
}

.alert-icon {
  width: 52px;
  height: 52px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.alert-text {
  flex: 1;
}

.alert-title {
  font-size: 16px;
  font-weight: 600;
  color: #263238;
}

.alert-desc {
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
}

.alert-badge {
  font-weight: 500;
}

/* Detail Card */
.detail-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.detail-panels {
  background: transparent !important;
}

.detail-panels >>> .v-expansion-panel {
  background: transparent !important;
}

.detail-panels >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.detail-header {
  padding: 20px 24px !important;
}

.detail-icon-wrapper {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #263238;
}

.detail-subtitle {
  font-size: 12px;
  color: #6b7280;
}

.detail-content {
  padding: 0 24px 24px !important;
}

/* Mini Stat Cards */
.stat-mini-card {
  text-align: center;
  padding: 20px;
  border-radius: 16px;
  height: 100%;
}

.stat-mini-blue { background: #f5f5f5; }
.stat-mini-red { background: #fef2f2; }
.stat-mini-orange { background: #fff4dc; }
.stat-mini-green { background: #e8f5e9; }

.stat-mini-value {
  font-size: 18px;
  font-weight: 700;
  color: #263238;
}

.stat-mini-label {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

/* Timeline */
.timeline-section {
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.timeline-title {
  font-size: 15px;
  font-weight: 600;
  color: #263238;
  display: flex;
  align-items: center;
}

.timeline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 0;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 200px;
}

.timeline-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-bottom: 12px;
  position: relative;
  z-index: 1;
}

.timeline-dot.start {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 0 0 4px #fef2f2;
}

.timeline-dot.middle {
  background: linear-gradient(135deg, #a86400 0%, #a86400 100%);
  box-shadow: 0 0 0 4px #fff4dc;
}

.timeline-dot.current {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
  box-shadow: 0 0 0 4px #e8f5e9;
}

.timeline-line {
  flex: 1;
  height: 3px;
  background: linear-gradient(90deg, #fecaca 0%, #e6b04d 50%, #81c784 100%);
  margin-top: 8px;
  border-radius: 2px;
}

.timeline-content {
  text-align: center;
}

.timeline-label {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
}

.timeline-date {
  font-size: 12px;
  color: #6b7280;
  display: block;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-content {
    padding: 24px;
  }

  .hero-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .hero-title {
    font-size: 22px;
  }

  .position-chip {
    width: 100%;
    justify-content: center;
  }

  .duration-boxes {
    flex-wrap: wrap;
  }

  .timeline {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  .timeline-item {
    flex-direction: row;
    max-width: none;
    gap: 16px;
    padding: 12px 0;
  }

  .timeline-dot {
    margin-bottom: 0;
  }

  .timeline-line {
    display: none;
  }

  .timeline-content {
    text-align: left;
  }

  .alert-content {
    flex-wrap: wrap;
  }

  .alert-badge {
    width: 100%;
    margin-top: 12px;
  }
}

@media (max-width: 600px) {
  .teaching-service-container {
    padding: 16px;
  }

  .hero-content {
    padding: 20px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 28px;
  }

  .duration-box {
    padding: 10px 14px;
  }

  .duration-number {
    font-size: 24px;
  }

  .progress-legend {
    flex-direction: column;
    gap: 8px;
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }

.info-card {
  animation: fadeInUp 0.6s ease forwards;
  animation-delay: 0.3s;
  opacity: 0;
}
</style>